import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, Globe2, IndianRupee, CheckCircle2, Briefcase, Gift, Sparkles, GraduationCap, CreditCard } from 'lucide-react';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import ParticlesBg from '@/components/common/ParticlesBg';
import { PROGRAMS as PROGRAMS_DATA } from '@/data/mockData';
import { PROGRAMS } from '@/constants/testIds';
import useRazorpayCheckout from '@/hooks/useRazorpayCheckout';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';

const testIdMap = {
  selenium: PROGRAMS.programSelenium,
  playwright: PROGRAMS.programPlaywright,
  appium: PROGRAMS.programAppium,
};

const ProgramsPage = () => {
  const { hash } = useLocation();
  const { user } = useAuth();
  const { launch } = useRazorpayCheckout();
  const [dbCourses, setDbCourses] = useState([]);

  useEffect(() => {
    api.get('/courses').then((r) => setDbCourses(r.data.items || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 200);
    }
  }, [hash]);

  const handlePay = (programId) => {
    const slug = { selenium: 'selenium-java-bdd-ai', playwright: 'playwright-js-bdd-ai', appium: 'appium-java-bdd-ai' }[programId];
    const course = dbCourses.find((c) => c.slug === slug);
    if (!course) { window.location.href = '/learning-hub'; return; }
    launch({ courseId: course.id, amountInr: course.price || 1, courseName: course.course_name });
  };

  return (
    <>
      <section className="relative py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 radial-glow" />
        <ParticlesBg count={30} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs tracking-[0.3em] uppercase text-brand-cyan/90 font-mono">AI Automation Programs</div>
          <h1 data-testid={PROGRAMS.pageHeading} className="font-display mt-3 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight max-w-4xl font-semibold">
            Three flagship programs. <span className="text-brand-cyan">Engineered for hire-ability.</span>
          </h1>
          <p className="mt-5 text-white/70 max-w-2xl leading-relaxed">
            Choose your stack. Live mentor-led sessions, AI integration modules, capstone projects, and certified job-ready outcomes.
          </p>
        </div>
      </section>

      <section className="pb-24 lg:pb-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          {PROGRAMS_DATA.map((p, idx) => (
            <motion.div
              key={p.id}
              id={p.id}
              data-testid={testIdMap[p.id]}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.5 }}
              className="relative glass rounded-3xl overflow-hidden"
            >
              <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-brand-cyan/5 blur-3xl" />
              <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 p-7 lg:p-10">
                <div className="lg:col-span-5">
                  <div className="font-mono text-xs tracking-widest uppercase text-brand-cyan/80">Program 0{idx + 1}</div>
                  <h2 className="font-display mt-2 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">{p.title}</h2>
                  <p className="mt-4 text-white/70 leading-relaxed">{p.summary}</p>

                  <div className="mt-6 grid grid-cols-3 gap-3">
                    <div className="glass rounded-lg p-3">
                      <Clock className="w-4 h-4 text-brand-cyan" />
                      <div className="text-[10px] tracking-widest uppercase font-mono text-white/50 mt-2">Duration</div>
                      <div className="text-sm font-semibold mt-0.5">{p.duration}</div>
                    </div>
                    <div className="glass rounded-lg p-3">
                      <Globe2 className="w-4 h-4 text-brand-cyan" />
                      <div className="text-[10px] tracking-widest uppercase font-mono text-white/50 mt-2">Mode</div>
                      <div className="text-sm font-semibold mt-0.5">{p.mode}</div>
                    </div>
                    <div className="glass rounded-lg p-3">
                      <IndianRupee className="w-4 h-4 text-brand-cyan" />
                      <div className="text-[10px] tracking-widest uppercase font-mono text-white/50 mt-2">Fees</div>
                      <div className="text-sm font-semibold mt-0.5">{p.price}</div>
                    </div>
                  </div>

                  <div className="mt-6 flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={() => handlePay(p.id)}
                      data-testid={`${PROGRAMS.registerButton}-${p.id}`}
                      className="inline-flex items-center gap-2 bg-brand-cyan text-black font-semibold px-5 py-3 rounded-lg btn-glow hover:bg-brand-cyanSoft transition"
                    >
                      <CreditCard className="w-4 h-4" />
                      {user ? `Pay & Enroll` : `Register Now`}
                    </button>
                    <a
                      href={`/contact?program=${p.id}`}
                      data-testid={`${PROGRAMS.detailsButton}-${p.id}`}
                      className="inline-flex items-center gap-2 glass px-5 py-3 rounded-lg font-semibold hover:bg-brand-cyan/10 transition"
                    >
                      Get More Details
                    </a>
                  </div>
                </div>

                <div className="lg:col-span-7">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Block icon={GraduationCap} title="Learning Outcomes">
                      {p.outcomes.map((o) => (
                        <li key={o} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-brand-cyan flex-shrink-0 mt-0.5" /><span>{o}</span></li>
                      ))}
                    </Block>
                    <Block icon={Sparkles} title="AI Integrations">
                      {p.aiIntegrations.map((o) => (
                        <li key={o} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-brand-cyan flex-shrink-0 mt-0.5" /><span>{o}</span></li>
                      ))}
                    </Block>
                    <Block icon={Briefcase} title="Career Opportunities">
                      {p.careers.map((o) => (
                        <li key={o} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-brand-cyan flex-shrink-0 mt-0.5" /><span>{o}</span></li>
                      ))}
                    </Block>
                    <Block icon={Gift} title="Bonuses">
                      {p.bonuses.map((o) => (
                        <li key={o} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-brand-cyan flex-shrink-0 mt-0.5" /><span>{o}</span></li>
                      ))}
                    </Block>
                  </div>

                  <div className="mt-6">
                    <div className="text-xs tracking-[0.3em] uppercase text-brand-cyan/80 font-mono mb-3">Curriculum</div>
                    <Accordion type="single" collapsible data-testid={`${PROGRAMS.curriculumAccordion}-${p.id}`} className="glass rounded-xl">
                      {p.curriculum.map((m, i) => (
                        <AccordionItem key={m.module} value={`${p.id}-${i}`} className="border-white/5 px-4 last:border-b-0">
                          <AccordionTrigger className="hover:no-underline py-4 text-left">
                            <span className="flex items-center gap-3">
                              <span className="text-brand-cyan font-mono text-xs">0{i + 1}</span>
                              <span className="font-semibold">{m.module}</span>
                            </span>
                          </AccordionTrigger>
                          <AccordionContent>
                            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 pl-8 pb-2 text-sm text-white/70">
                              {m.topics.map((t) => <li key={t} className="flex items-start gap-2"><span className="text-brand-cyan/70 mt-2 w-1 h-1 rounded-full bg-brand-cyan flex-shrink-0" />{t}</li>)}
                            </ul>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>
    </>
  );
};

const Block = ({ icon: Icon, title, children }) => (
  <div className="glass rounded-xl p-5">
    <div className="flex items-center gap-2 mb-3">
      <Icon className="w-4 h-4 text-brand-cyan" />
      <div className="text-[11px] tracking-widest uppercase font-mono text-brand-cyan/90">{title}</div>
    </div>
    <ul className="space-y-2 text-sm text-white/75">{children}</ul>
  </div>
);

export default ProgramsPage;
